// Loading / empty / error presentational states reused by every queue (app.md §8).
import { Body1, Button, Spinner, Subtitle2, makeStyles, mergeClasses, tokens } from '@fluentui/react-components'
import { DismissRegular } from '@fluentui/react-icons'

const useStyles = makeStyles({
  center: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '8px',
    padding: '64px 24px',
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
  },
  errorBanner: {
    color: tokens.colorPaletteRedForeground1,
    backgroundColor: tokens.colorPaletteRedBackground1,
    border: `1px solid ${tokens.colorPaletteRedBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    marginBottom: '16px',
  },
  warningBanner: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    color: tokens.colorPaletteYellowForeground2,
    backgroundColor: tokens.colorPaletteYellowBackground1,
    border: `1px solid ${tokens.colorPaletteYellowBorder1}`,
    borderRadius: tokens.borderRadiusMedium,
    padding: '12px 16px',
    marginBottom: '16px',
  },
  warningText: { flex: 1 },
  withAction: { display: 'flex', alignItems: 'center', gap: '12px' },
})

export function Loading({ label = 'Loading…' }: { label?: string }) {
  const s = useStyles()
  return (
    <div className={s.center}>
      <Spinner label={label} />
    </div>
  )
}

/** The agreed empty text (app.md §8). */
export function EmptyQueue({ text = 'You’re all caught-up.' }: { text?: string }) {
  const s = useStyles()
  return (
    <div className={s.center}>
      <Subtitle2>{text}</Subtitle2>
    </div>
  )
}

/** A failure, in red. With `onRetry`, a Retry button beside it — for a read
    that failed and can simply be asked again. With `alert`, it is announced
    when it appears (`role="alert"`), for a failure the reviewer's own action
    caused while focus stays on a field; `id` lets that field name it as its
    description (QA 2026-10-04, C1-09). */
export function ErrorBanner({
  message,
  onRetry,
  alert = false,
  id,
}: {
  message: string | null
  onRetry?: () => void
  alert?: boolean
  id?: string
}) {
  const s = useStyles()
  if (!message) return null
  const a11y = { id, role: alert ? 'alert' : undefined }
  if (!onRetry) return <div {...a11y} className={s.errorBanner}><Body1>{message}</Body1></div>
  return (
    <div {...a11y} className={mergeClasses(s.errorBanner, s.withAction)}>
      <Body1 className={s.warningText}>{message}</Body1>
      <Button size="small" onClick={onRetry}>Retry</Button>
    </div>
  )
}

/** Dismissible warning (e.g. "couldn't refresh holds", or a filter hiding rows
    a batch action still covers). */
export function WarningBanner({ message, onDismiss }: { message: string | null; onDismiss?: () => void }) {
  const s = useStyles()
  if (!message) return null
  return (
    <div className={s.warningBanner}>
      <Body1 className={s.warningText}>{message}</Body1>
      {onDismiss && (
        <Button appearance="subtle" size="small" icon={<DismissRegular />} aria-label="Dismiss" onClick={onDismiss} />
      )}
    </div>
  )
}
